import { useEffect, useRef } from 'react';
import type { AudioHTMLAttributes } from 'react';
import {
  claimMediaFocus,
  onMediaFocusClaim,
  releaseMediaFocus,
  type MediaSurface,
} from '../hooks/mediaFocus';

type Props = AudioHTMLAttributes<HTMLAudioElement> & {
  /** Which surface owns this element — used to arbitrate playback against
   *  the TTS singleton and other gallery audio elements. */
  surface: MediaSurface;
};

/**
 * Stage 12 (A4): drop-in replacement for `<audio controls />` that
 * cooperates with the media-focus bus.
 *
 *   • Claims focus on actual playback (not on mute / seek / load).
 *   • Releases focus on pause / ended / error.
 *   • Subscribes to the bus and pauses itself when a different surface claims.
 *
 * Use the same props as the native element. No behavioural change when
 * only one audio surface is active.
 */
export function FocusedAudio({ surface, ...audioProps }: Props): JSX.Element {
  const ref = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onPlay = (): void => claimMediaFocus(surface);
    const onPauseOrEnd = (): void => releaseMediaFocus(surface);
    el.addEventListener('playing', onPlay);
    el.addEventListener('pause', onPauseOrEnd);
    el.addEventListener('ended', onPauseOrEnd);
    el.addEventListener('error', onPauseOrEnd);
    const unsubscribe = onMediaFocusClaim(({ owner }) => {
      if (owner !== surface && !el.paused) {
        try {
          el.pause();
        } catch {
          /* ignore */
        }
      }
    });
    return () => {
      el.removeEventListener('playing', onPlay);
      el.removeEventListener('pause', onPauseOrEnd);
      el.removeEventListener('ended', onPauseOrEnd);
      el.removeEventListener('error', onPauseOrEnd);
      unsubscribe();
    };
  }, [surface]);

  return <audio ref={ref} {...audioProps} />;
}
